import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../Config.js';
import styles from './UserCashCollection.module.css';
import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  Banknote,
  CalendarRange,
  CalendarSearch,
  CreditCard,
  Download,
  RefreshCw,
  Smartphone,
  TriangleAlert,
  Undo2,
  Wallet,
  X,
} from 'lucide-react';

const PERIOD_FILTERS = ['Today', 'This Week', 'This Month', 'Previous Month', 'This FY', 'Custom Date'];

const SUMMARY_CARDS = [
  { key: 'final_cash_collection', label: 'Total collection', icon: Wallet, primary: true },
  { key: 'cash_collection', label: 'Cash', icon: Banknote },
  { key: 'upi_collection', label: 'UPI', icon: Smartphone },
  { key: 'card_collection', label: 'Card', icon: CreditCard },
  { key: 'cash_return', label: 'Cash out', icon: Undo2 },
];

// Amount with Indian digit grouping, for display only (the Excel export keeps raw numbers)
const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// True when an axios error is the result of an aborted/cancelled request.
const isCanceledError = (err) =>
  axios.isCancel?.(err) ||
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError';

const UserCashCollection = () => {
  const [userCashCollection, setUserCashCollection] = useState([]);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeFilter, setActiveFilter] = useState('Today');

  // FY-grouped quarters (same as BillViewer)
  const [quarters, setQuarter] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState('');

  // Loading + cancel control for the collection GET call.
  const [isLoading, setIsLoading] = useState(false);
  const fetchAbortRef = useRef(null);

  // Custom date modal
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Format any date value as dd-MM-yyyy.
  const formatDMY = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return format(d, 'dd-MM-yyyy');
  };

  /* ---------------- Cancellable fetch helpers ---------------- */
  const beginFetch = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setIsLoading(true);
    return controller;
  };

  const endFetch = (controller) => {
    if (fetchAbortRef.current === controller) {
      fetchAbortRef.current = null;
      setIsLoading(false);
    }
  };

  const cancelFetch = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
      fetchAbortRef.current = null;
    }
    setIsLoading(false);
  };

  const fetchUserCashCollection = async () => {
    const userData = JSON.parse(sessionStorage.getItem('user'));
    const storeId = userData?.storeId;

    if (!storeId || !startDate || !endDate) {
      console.error('Store ID, start date, or end date not found');
      return;
    }

    const controller = beginFetch();
    try {
      const response = await axios.get(`${API_BASE_URL}/user/getUserCashCollection`, {
        params: { storeId, startDate, endDate },
        signal: controller.signal,
      });
      setUserCashCollection(response.data || []);
      setError('');
    } catch (err) {
      if (isCanceledError(err)) return; // cancelled — discard silently
      setError('An error occurred while fetching cash collection.');
    } finally {
      endFetch(controller);
    }
  };

  useEffect(() => {
    setUserCashCollection([]);
    if (startDate && endDate) {
      fetchUserCashCollection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // First mount: build quarters and default to Today.
  useEffect(() => {
    setQuarter(getIndianFYQuarters());
    handleDateFilter('Today');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any pending fetch if the component unmounts.
  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
        fetchAbortRef.current = null;
      }
    };
  }, []);

  // Build current + previous FY quarters, each tagged with its FY group label.
  const getIndianFYQuarters = () => {
    const today = new Date();
    const currentFyStart = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();

    const makeQuarters = (startYear, group) => ({
      [`${group}-Q1`]: { label: `Q1 · Apr–Jun ${startYear}`, start: `${startYear}-04-01`, end: `${startYear}-06-30`, group },
      [`${group}-Q2`]: { label: `Q2 · Jul–Sep ${startYear}`, start: `${startYear}-07-01`, end: `${startYear}-09-30`, group },
      [`${group}-Q3`]: { label: `Q3 · Oct–Dec ${startYear}`, start: `${startYear}-10-01`, end: `${startYear}-12-31`, group },
      [`${group}-Q4`]: { label: `Q4 · Jan–Mar ${startYear + 1}`, start: `${startYear + 1}-01-01`, end: `${startYear + 1}-03-31`, group },
    });

    const fyLabel = (start) => `FY ${start}-${String(start + 1).slice(-2)}`;

    return {
      ...makeQuarters(currentFyStart, fyLabel(currentFyStart)),
      ...makeQuarters(currentFyStart - 1, fyLabel(currentFyStart - 1)),
    };
  };

  // Group quarters by their FY label so we can render <optgroup> separators.
  const quarterGroups = quarters
    ? Object.entries(quarters).reduce((acc, [key, q]) => {
        (acc[q.group] = acc[q.group] || []).push([key, q]);
        return acc;
      }, {})
    : {};

  const handleQuarterChange = (quarterKey) => {
    if (!quarters || !quarterKey) {
      setSelectedQuarter('');
      return;
    }
    const selected = quarters[quarterKey];
    if (selected) {
      setStartDate(selected.start);
      setEndDate(selected.end);
      setActiveFilter('Quarter');
      setSelectedQuarter(quarterKey);
    }
  };

  const handleDateFilter = (filter) => {
    const today = new Date();
    let calculatedStartDate = '';
    let calculatedEndDate = format(today, 'yyyy-MM-dd');
    setSelectedQuarter('');

    switch (filter) {
      case 'Today':
        calculatedStartDate = calculatedEndDate;
        break;
      case 'This Week': {
        const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
        calculatedStartDate = format(startOfWeek, 'yyyy-MM-dd');
        break;
      }
      case 'This Month':
        calculatedStartDate = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
        break;
      case 'Previous Month':
        calculatedStartDate = format(new Date(today.getFullYear(), today.getMonth() - 1, 1), 'yyyy-MM-dd');
        calculatedEndDate = format(new Date(today.getFullYear(), today.getMonth(), 0), 'yyyy-MM-dd');
        break;
      case 'This FY': {
        const currentFYStartMonth = today.getMonth() >= 3 ? 3 : -9;
        calculatedStartDate = format(new Date(today.getFullYear(), currentFYStartMonth, 1), 'yyyy-MM-dd');
        const endYear = currentFYStartMonth === -9 ? today.getFullYear() : today.getFullYear() + 1;
        calculatedEndDate = format(new Date(endYear, 2, 31), 'yyyy-MM-dd');
        break;
      }
      case 'Custom Date':
        // handled via the modal (openCustomDateModal); nothing to do here.
        return;
      default:
        return;
    }

    setStartDate(calculatedStartDate);
    setEndDate(calculatedEndDate);
    setActiveFilter(filter);
  };

  // Custom date opens a modal with a Find button (same as BillViewer).
  const openCustomDateModal = () => {
    setCustomStart(activeFilter === 'Custom Date' ? startDate : '');
    setCustomEnd(activeFilter === 'Custom Date' ? endDate : '');
    setShowCustomDateModal(true);
  };

  const applyCustomDate = () => {
    if (!customStart || !customEnd) {
      alert('Please select both start and end dates.');
      return;
    }
    if (customStart > customEnd) {
      alert('Start date cannot be after end date.');
      return;
    }
    setSelectedQuarter('');
    setActiveFilter('Custom Date');
    setStartDate(customStart);
    setEndDate(customEnd);
    setShowCustomDateModal(false);
  };

  const calculateTotal = (key) =>
    userCashCollection.reduce((total, item) => total + (item[key] || 0), 0).toFixed(2);

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Cash Collection');

    const formattedStartDate = startDate ? formatDMY(startDate) : 'N/A';
    const formattedEndDate = endDate ? formatDMY(endDate) : 'N/A';

    worksheet.columns = [
      { key: 'userId', width: 12 },
      { key: 'collectionDate', width: 18 },
      { key: 'userName', width: 20 },
      { key: 'cashCollection', width: 15, style: { numFmt: '#,##0.00' } },
      { key: 'upiCollection', width: 15, style: { numFmt: '#,##0.00' } },
      { key: 'cardCollection', width: 15, style: { numFmt: '#,##0.00' } },
      { key: 'cashOut', width: 12, style: { numFmt: '#,##0.00' } },
      { key: 'total', width: 15, style: { numFmt: '#,##0.00' } },
    ];

    worksheet.addRow([]);
    const titleRow = worksheet.addRow(['User Cash Collection Report']);
    const periodRow = worksheet.addRow([`Period: ${formattedStartDate} to ${formattedEndDate}`]);
    worksheet.addRow([]);

    titleRow.height = 30;
    titleRow.font = { bold: true, size: 16 };
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`A2:H2`);

    periodRow.height = 25;
    periodRow.font = { bold: true, size: 12 };
    periodRow.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`A3:H3`);

    const headerBgColor = 'D9E1F2';
    const totalBgColor = 'FFEB9C';

    const headerRow = worksheet.addRow([
      'User ID', 'Collection Date', 'User Name', 'Cash Collection',
      'UPI Collection', 'Card Collection', 'Cash Out', 'Total',
    ]);

    headerRow.height = 25;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerBgColor } };
      cell.border = {
        top: { style: 'medium', color: { argb: '000000' } },
        left: { style: 'medium', color: { argb: headerBgColor } },
        bottom: { style: 'medium', color: { argb: '000000' } },
        right: { style: 'medium', color: { argb: headerBgColor } },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    userCashCollection.forEach((item) => {
      const row = worksheet.addRow({
        userId: item.userId || '',
        collectionDate: formatDMY(item.collection_date) || '',
        userName: item.userName || '',
        cashCollection: Number(item.cash_collection || 0),
        upiCollection: Number(item.upi_collection || 0),
        cardCollection: Number(item.card_collection || 0),
        cashOut: Number(item.cash_return || 0),
        total: Number(item.final_cash_collection || 0),
      });

      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
        };
        if (cell.col >= 4) cell.alignment = { horizontal: 'right' };
      });
    });

    if (userCashCollection.length > 0) {
      const totals = {
        userId: 'Total',
        collectionDate: '',
        userName: '',
        cashCollection: parseFloat(calculateTotal('cash_collection')),
        upiCollection: parseFloat(calculateTotal('upi_collection')),
        cardCollection: parseFloat(calculateTotal('card_collection')),
        cashOut: parseFloat(calculateTotal('cash_return')),
        total: parseFloat(calculateTotal('final_cash_collection')),
      };

      const totalRow = worksheet.addRow(totals);
      worksheet.mergeCells(`A${totalRow.number}:C${totalRow.number}`);

      totalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: totalBgColor } };
        cell.border = {
          top: { style: 'medium', color: { argb: '000000' } },
          left: { style: 'medium', color: { argb: totalBgColor } },
          bottom: { style: 'medium', color: { argb: '000000' } },
          right: { style: 'medium', color: { argb: totalBgColor } },
        };
        if (cell.col >= 4) cell.alignment = { horizontal: 'right' };
      });

      worksheet.getCell(`A${totalRow.number}`).alignment = { horizontal: 'center' };
    }

    const fileName = `Cash_Collection_Report.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
  };

  // Close the custom date modal on Escape
  useEffect(() => {
    if (!showCustomDateModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowCustomDateModal(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCustomDateModal]);

  const hasData = userCashCollection.length > 0;
  const periodLabel =
    activeFilter === 'Quarter' ? quarters?.[selectedQuarter]?.label : activeFilter;
  const closeCustomDateModal = () => setShowCustomDateModal(false);

  return (
    <>
      {/* Custom date modal — rendered outside the page so it covers the whole dashboard */}
      {showCustomDateModal && (
        <div className={styles.modalOverlay} onClick={closeCustomDateModal}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cash-custom-date-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3 id="cash-custom-date-title" className={styles.modalTitle}>
                  Select date range
                </h3>
                <p className={styles.modalSubtitle}>Show cash collection between two dates.</p>
              </div>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={closeCustomDateModal}
                aria-label="Close"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.dateGrid}>
                <div className={styles.field}>
                  <label htmlFor="cash-start-date" className={styles.label}>
                    Start date
                  </label>
                  <input
                    id="cash-start-date"
                    type="date"
                    className={styles.input}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="cash-end-date" className={styles.label}>
                    End date
                  </label>
                  <input
                    id="cash-end-date"
                    type="date"
                    className={styles.input}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={closeCustomDateModal}>
                Cancel
              </button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={applyCustomDate}>
                Find
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.page}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <span className={styles.headerIcon} aria-hidden="true">
              <Wallet size={20} />
            </span>
            <h1 className={styles.title}>User Cash Collection</h1>
            <div className={styles.chips}>
              {periodLabel && <span className={styles.chip}>{periodLabel}</span>}
              {startDate && endDate ? (
                <span className={styles.chip}>
                  <CalendarRange size={13} aria-hidden="true" />
                  {formatDMY(startDate)} → {formatDMY(endDate)}
                </span>
              ) : (
                <span className={styles.chipMuted}>No period selected</span>
              )}
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={fetchUserCashCollection}
              disabled={isLoading || !startDate || !endDate}
            >
              <RefreshCw size={15} className={isLoading ? styles.spin : undefined} aria-hidden="true" />
              Refresh
            </button>
            {hasData && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={exportToExcel}
                disabled={isLoading}
              >
                <Download size={15} aria-hidden="true" />
                Export to Excel
              </button>
            )}
          </div>
        </header>

        {error && (
          <div className={styles.errorBanner} role="alert">
            <TriangleAlert size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* -------------------- Summary cards -------------------- */}
        <div className={styles.summaryGrid}>
          {SUMMARY_CARDS.map(({ key, label, icon: Icon, primary }) => (
            <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
              <span className={styles.summaryIcon} aria-hidden="true">
                <Icon size={18} />
              </span>
              <div className={styles.summaryText}>
                <span className={styles.summaryLabel}>{label}</span>
                <span className={styles.summaryValue}>₹{formatAmount(calculateTotal(key))}</span>
              </div>
            </div>
          ))}
        </div>

        {/* -------------------- Results card -------------------- */}
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.filters}>
              {PERIOD_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`${styles.btn} ${styles.btnToggle} ${activeFilter === filter ? styles.btnToggleOn : ''}`}
                  aria-pressed={activeFilter === filter}
                  onClick={() => (filter === 'Custom Date' ? openCustomDateModal() : handleDateFilter(filter))}
                  disabled={isLoading}
                >
                  {filter}
                </button>
              ))}
            </div>

            {quarters && (
              <select
                className={`${styles.control} ${selectedQuarter ? styles.controlActive : ''}`}
                value={selectedQuarter}
                onChange={(e) => handleQuarterChange(e.target.value)}
                disabled={isLoading}
                aria-label="Quarter"
              >
                <option value="">Select Quarter</option>
                {Object.entries(quarterGroups).map(([group, entries]) => (
                  <optgroup key={group} label={group}>
                    {entries.map(([key, q]) => (
                      <option key={key} value={key}>
                        {q.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>

          <div className={styles.tableWrap}>
            {isLoading && (
              <div className={styles.loadingOverlay}>
                <span className={styles.spinner} aria-hidden="true" />
                <span className={styles.loadingText}>Loading cash collection…</span>
                <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={cancelFetch}>
                  Cancel
                </button>
              </div>
            )}

            {hasData ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Collection Date</th>
                    <th>User Name</th>
                    <th className={styles.num}>Cash Collection</th>
                    <th className={styles.num}>UPI Collection</th>
                    <th className={styles.num}>Card Collection</th>
                    <th className={styles.num}>Cash Out</th>
                    <th className={styles.num}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {userCashCollection.map((item) => (
                    <tr key={`${item.userId}-${item.collection_date}`}>
                      <td className={styles.code}>{item.userId}</td>
                      <td>{formatDMY(item.collection_date)}</td>
                      <td className={styles.strong}>{item.userName}</td>
                      <td className={styles.num}>{formatAmount(item.cash_collection)}</td>
                      <td className={styles.num}>{formatAmount(item.upi_collection)}</td>
                      <td className={styles.num}>{formatAmount(item.card_collection)}</td>
                      <td className={styles.num}>{formatAmount(item.cash_return)}</td>
                      <td className={`${styles.num} ${styles.strong}`}>{formatAmount(item.final_cash_collection)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Total</td>
                    <td className={styles.num}>{formatAmount(calculateTotal('cash_collection'))}</td>
                    <td className={styles.num}>{formatAmount(calculateTotal('upi_collection'))}</td>
                    <td className={styles.num}>{formatAmount(calculateTotal('card_collection'))}</td>
                    <td className={styles.num}>{formatAmount(calculateTotal('cash_return'))}</td>
                    <td className={styles.num}>{formatAmount(calculateTotal('final_cash_collection'))}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              !isLoading && (
                <div className={styles.noResults}>
                  <span className={styles.noResultsIcon} aria-hidden="true">
                    <CalendarSearch size={28} />
                  </span>
                  <p className={styles.noResultsTitle}>No cash collection found</p>
                  <p className={styles.noResultsText}>
                    Nothing was collected in this period. Try another period or quarter.
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserCashCollection;